import { LabelType, Options } from '@angular-slider/ngx-slider';
import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { AbstractValueAccessor } from '../../../utils/AbstractValueAccessor';
import { maxBy, minBy } from '../../../utils/array';
import { valueAccessorProvider } from '../../../utils/provider-builders';
import { IdScope } from '../../../utils/string';
import { AgeTargetGroup, GenderTargetGroup, TargetGroup } from '../../../woo_services.module/shared-types';

@Component({
  selector: 'target-group-select',
  templateUrl: './target-group-select.component.html',
  styleUrls: ['./target-group-select.component.scss'],
  providers: [valueAccessorProvider(TargetGroupSelect)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TargetGroupSelect extends AbstractValueAccessor<TargetGroupSelected> {
  genders = input<GenderTargetGroup[]>([]);
  ages = input<AgeTargetGroup[]>([]);

  /* Needed to set correct value in checkboxes, because of two components in same view */

  singleGender = signal<boolean>(false);
  ageRequired = signal<boolean>(false);
  ageMin = signal<number>(20);
  ageMax = signal<number>(29);
  sliderOptions: Options = {
    disabled: false,
    draggableRange: true,
    floor: 15,
    ceil: 80,
    step: 5,
    minRange: 10,
    translate: (value, label) => {
      if (label === LabelType.High || label === LabelType.Ceil) {
        value -= 1;
      }
      return value + ' år';
    },
  };
  selectedGender = signal<GenderTargetGroup | null>(null);
  idScope = null;

  constructor() {
    super();
    this.idScope = new IdScope();
  }

  setSliderSpan = (ages: AgeTargetGroup[]): void => {
    this.sliderOptions = { ...this.sliderOptions, floor: minBy(ages, 'min_age'), ceil: maxBy(ages, 'max_age') + 1 };
  };

  onSliderChange(event: { value: number; highValue: number }): void {
    this.ageMin.set(event.value);
    this.ageMax.set(event.highValue);
    this.change();
  }

  change(): void {
    const targetGroup = { genders: this.genders(), ages: [], choices: null };
    targetGroup.choices = { gender: this.singleGender(), age: this.ageRequired() };

    if (this.singleGender()) {
      targetGroup.genders = this.selectedGender() === null ? [] : [this.selectedGender()];
    }
    if (this.ageRequired()) {
      const ages = this.ages()
        .filter((age) => age.min_age >= this.ageMin())
        .filter((age) => age.max_age <= this.ageMax());
      targetGroup.ages = ages;
    }

    this.model = targetGroup;
  }

  writeValue(model: TargetGroupSelected): void {
    if (model) {
      model.choices = model.choices ? model.choices : { gender: false, age: false };
      this.singleGender.set(model.choices.gender);
      this.ageRequired.set(model.choices.age);
      this.selectedGender.set(this.singleGender() ? model.genders[0] || null : null);
      this.sliderOptions = { ...this.sliderOptions, disabled: !this.ageRequired() };
      if (model.ages.length > 0) {
        this.ageMin.set(Math.min(...model.ages.map((age) => age.min_age)));
        this.ageMax.set(Math.max(...model.ages.map((age) => age.max_age)));
      }
      super.writeValue(model);
    }
  }

  toggleAgeRequired(): void {
    this.ageRequired.set(!this.ageRequired());
    this.sliderOptions = { ...this.sliderOptions, disabled: !this.ageRequired() };
    this.change();
  }

  toggleSingleGender(): void {
    this.singleGender.set(!this.singleGender());
    this.selectedGender.set(null);
    this.change();
  }
}

export interface TargetGroupSelected extends TargetGroup {
  choices: TargetGroupChoices;
}

export interface TargetGroupChoices {
  gender: boolean;
  age: boolean;
}
